import { useState, useCallback, useRef } from 'react';
import { demandeService } from '../services/demande.service';

/**
 * Hook personnalisé pour gérer les données des formulaires des étapes de projet
 * 
 * Ce hook fournit :
 * - Gestion de l'état des formulaires
 * - Validation des données
 * - Soumission des demandes
 * - Persistance des données entre les étapes
 */
export const useFormData = (projectId) => {
  // État des données du formulaire
  const [formData, setFormData] = useState({
    // Données personnelles
    name: '',
    numero_tel: '',
    email: '',
    
    // Données de base du projet
    projet: null,
    reponse: null,
    adresse: '',
    reference_chantier: '',
    post_code: '',
    project_step: '',
    project_start_date: '',
    construction_site_ref: '',
    has_technician: false,
    need_technician: false,
    complementary_info: '',
    submitted_by: null,
    documents: [],
    
    // Données spécifiques aux étapes
    stepData: {},
    
    // État de soumission
    isSubmitting: false,
    submitError: null,
    submitSuccess: false,
  });

  // Référence pour éviter les re-renders inutiles
  const formDataRef = useRef(formData);

  // Mettre à jour les données du formulaire
  const updateFormData = useCallback((field, value) => {
    setFormData(prevData => {
      const newData = {
        ...prevData,
        [field]: value,
      };
      formDataRef.current = newData;
      return newData;
    });
  }, []);

  const [errorMessage, setErrorMessage] = useState("");

  // Mettre à jour les données d'une étape spécifique
  const updateStepData = useCallback((stepId, stepData) => {
    setFormData(prevData => {
      const newData = {
        ...prevData,
        stepData: {
          ...prevData.stepData,
          [stepId]: stepData,
        },
      };
      formDataRef.current = newData;
      return newData;
    });
  }, []);

  // Ajouter un élément sélectionné dans une section
  const addSelectedElement = useCallback((stepId, sectionId, elementId, elementData) => {
    console.log('=== addSelectedElement appelé ===');
    console.log('stepId:', stepId);
    console.log('sectionId:', sectionId);
    console.log('elementId:', elementId);
    console.log('elementData:', elementData);
    
    setFormData(prevData => {
      const newData = { ...prevData };
      console.log('Données précédentes:', prevData);
      
      // Initialiser la structure si elle n'existe pas
      if (!newData.stepData[stepId]) {
        newData.stepData[stepId] = {};
        console.log('Initialisation de stepData pour stepId:', stepId);
      }
      if (!newData.stepData[stepId].sections) {
        newData.stepData[stepId].sections = {};
        console.log('Initialisation de sections pour stepId:', stepId);
      }
      if (!newData.stepData[stepId].sections[sectionId]) {
        newData.stepData[stepId].sections[sectionId] = [];
        console.log('Initialisation de sectionId:', sectionId);
      }
      
      // Ajouter l'élément s'il n'existe pas déjà
      const existingIndex = newData.stepData[stepId].sections[sectionId].findIndex(
        item => item.id === elementId
      );
      
      if (existingIndex >= 0) {
        // Mettre à jour l'élément existant
        newData.stepData[stepId].sections[sectionId][existingIndex] = {
          id: elementId,
          ...elementData,
        };
        console.log('Élément mis à jour à l\'index:', existingIndex);
      } else {
        // Ajouter un nouvel élément
        newData.stepData[stepId].sections[sectionId].push({
          id: elementId,
          ...elementData,
        });
        console.log('Nouvel élément ajouté');
      }
      
      console.log('Nouvelles données après ajout:', newData);
      formDataRef.current = newData;
      return newData;
    });
  }, []);

  // Supprimer un élément sélectionné d'une section
  const removeSelectedElement = useCallback((stepId, sectionId, elementId) => {
    setFormData(prevData => {
      const newData = { ...prevData };
      
      if (newData.stepData[stepId]?.sections?.[sectionId]) {
        newData.stepData[stepId].sections[sectionId] = newData.stepData[stepId].sections[sectionId].filter(
          item => item.id !== elementId
        );
      }
      
      formDataRef.current = newData;
      return newData;
    });
  }, []);

  // Récupérer les éléments sélectionnés d'une section
  const getSelectedElements = useCallback((stepId, sectionId) => {
    return formData.stepData[stepId]?.sections?.[sectionId] || [];
  }, [formData.stepData]);

  // Récupérer tous les éléments sélectionnés de toutes les étapes
  const getAllSelectedElements = useCallback(() => {
    const allElements = [];
    
    const currentFormData = formDataRef.current;
    Object.keys(currentFormData.stepData).forEach(stepId => {
      if (currentFormData.stepData[stepId].sections) {
        Object.keys(currentFormData.stepData[stepId].sections).forEach(sectionId => {
          currentFormData.stepData[stepId].sections[sectionId].forEach(element => {
            allElements.push({
              stepId,
              sectionId,
              ...element,
            });
          });
        });
      }
    });
    
    return allElements;
  }, []);

  // Récupérer les données d'une étape
  const getStepData = useCallback((stepId) => {
    return formDataRef.current.stepData[stepId] || {};
  }, []);

  // Valider les données du formulaire
  const validateFormData = useCallback(() => {
    const currentFormData = formDataRef.current;
    const errors = [];
    
    // Validation des champs personnels requis
    if (!currentFormData.name) {
      errors.push('Le nom est requis');
    }
    
    if (!currentFormData.email) {
      errors.push('L\'email est requis');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(currentFormData.email)) {
      errors.push('L\'email doit être valide');
    }
    
    if (!currentFormData.numero_tel) {
      errors.push('Le numéro de téléphone est requis');
    }
    
    // Validation des champs du projet
    if (!currentFormData.adresse) {
      errors.push('L\'adresse du chantier est requise');
    }
    
    if (!currentFormData.project_step) {
      errors.push('L\'étape du projet est requise');
    }
    
    if (currentFormData.post_code && isNaN(parseInt(currentFormData.post_code))) {
      errors.push('Le code postal doit être un nombre valide');
    }
    
    if (currentFormData.project_start_date) {
      const date = new Date(currentFormData.project_start_date);
      if (isNaN(date.getTime())) {
        errors.push('La date de début du projet doit être valide');
      }
    }
    
    // Validation des longueurs maximales
    if (currentFormData.name && currentFormData.name.length > 255) {
      errors.push('Le nom ne peut pas dépasser 255 caractères');
    }
    
    if (currentFormData.numero_tel && currentFormData.numero_tel.length > 255) {
      errors.push('Le numéro de téléphone ne peut pas dépasser 255 caractères');
    }
    
    if (currentFormData.email && currentFormData.email.length > 255) {
      errors.push('L\'email ne peut pas dépasser 255 caractères');
    }
    
    return {
      isValid: errors.length === 0,
      errors,
    };
  }, []);

  // Soumettre la demande
  const submitDemande = useCallback(async (demandeDataFromComponent) => {
    try {
      setFormData(prev => ({ ...prev, isSubmitting: true, submitError: null }));
      
      // Validation des données
      const validation = validateFormData(demandeDataFromComponent);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }
      
      // Nettoyer l'ID du projet
      const cleanProjectId = Array.isArray(projectId) 
        ? (projectId.length > 0 ? projectId[0] : null)
        : projectId;
      
      // Utiliser directement les données passées depuis le composant
      const demandeData = {
        ...demandeDataFromComponent,
        projet: cleanProjectId,
      };
      
      console.log('=== VÉRIFICATION DES SÉLECTIONS ===');
      console.log('selectedElements dans demandeData:', demandeData.selectedElements);
      console.log('demandeData.selectedElements:', demandeData.selectedElements);
      
      console.log('Données de la demande avant soumission:', demandeData);
      console.log('Type de projet ID:', typeof demandeData.projet);
      
      // Soumettre la demande
      const result = await demandeService.createDemande(demandeData);
      
      if (result.success) {
        setFormData(prev => ({ 
          ...prev, 
          isSubmitting: false, 
          submitSuccess: true,
          submitError: null 
        }));
        return result;
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      // const errors = error.response?.data;
      // const formatted = Object.entries(errors)
      //   .map(([field, messages]) => `${field} : ${messages.join(", ")}`)
      //   .join("\n");

      // setErrorMessage(formatted);
      console.error('Erreur lors de la soumissionnnn de la demande:', error.response);
      setFormData(prev => ({ 
        ...prev, 
        isSubmitting: false, 
        submitError: error.message,
        submitSuccess: false 
      }));
      return {
        success: false,
        error: error.message,
      };
    }
  }, [validateFormData, projectId]);

  // Réinitialiser le formulaire
  const resetForm = useCallback(() => {
    setFormData({
      name: '',
      numero_tel: '',
      email: '',
      projet: null,
      reponse: null,
      adresse: '',
      reference_chantier: '',
      post_code: '',
      project_step: '',
      project_start_date: '',
      construction_site_ref: '',
      has_technician: false,
      need_technician: false,
      complementary_info: '',
      submitted_by: null,
      documents: [],
      stepData: {},
      isSubmitting: false,
      submitError: null,
      submitSuccess: false,
    });
    formDataRef.current = formData;
  }, [projectId]);

  // Sauvegarder les données dans le localStorage
  const saveToLocalStorage = useCallback(() => {
    try {
      const dataToSave = {
        projectId,
        formData: formDataRef.current,
        timestamp: new Date().toISOString(),
      };
      localStorage.setItem(`project_form_${projectId}`, JSON.stringify(dataToSave));
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des données:', error);
    }
  }, [projectId]);

  // Charger les données depuis le localStorage
  const loadFromLocalStorage = useCallback(() => {
    try {
      const savedData = localStorage.getItem(`project_form_${projectId}`);
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        if (parsedData.projectId === projectId) {
          setFormData(parsedData.formData);
          formDataRef.current = parsedData.formData;
          return true;
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
    }
    return false;
  }, [projectId]);

  // Nettoyer les données sauvegardées
  const clearSavedData = useCallback(() => {
    try {
      localStorage.removeItem(`project_form_${projectId}`);
    } catch (error) {
      console.error('Erreur lors du nettoyage des données:', error);
    }
  }, [projectId]);

  return {
    // Données du formulaire
    formData,
    
    // Actions de base
    updateFormData,
    updateStepData,
    getStepData,
    validateFormData,
    submitDemande,
    resetForm,
    
    // Gestion des éléments sélectionnés
    addSelectedElement,
    removeSelectedElement,
    getSelectedElements,
    getAllSelectedElements,
    
    // Persistance
    saveToLocalStorage,
    loadFromLocalStorage,
    clearSavedData,
    
    // État de soumission
    isSubmitting: formData.isSubmitting,
    submitError: formData.submitError,
    submitSuccess: formData.submitSuccess,
  };
};

export default useFormData;